import { useCallback, useEffect, useMemo, useState } from 'react';
import { EUploadStatus } from '../types/bite';
import { useDispatch, useSelector } from 'react-redux';
import { introSubtitlesLinesSelector } from '../store/createBite/createBite.selectors';
import { ILine, ISubtitles, ISubtitlesLines } from '../types/subtitles';
import formatSubtitles from '../utils/formatSubtitles';
import { fetchIntroSubtitlesLines } from '../store/createBite/createBites.actions';
import { useIsFocused } from '@react-navigation/native';

const useIntroSubtitles = (subtitlesToDisplay?: ISubtitles) => {
  const dispatch = useDispatch();
  const isFocused = useIsFocused();

  const linesOriginalData: ISubtitlesLines = useSelector(introSubtitlesLinesSelector);

  const [linesData, setLinesData] = useState<ILine[]>(null);
  const [initialLinesData, setInitialLinesData] = useState<ILine[]>(null);

  const subtitlesAvailable = useMemo(
    () => subtitlesToDisplay?.upload_status === EUploadStatus.done,
    [subtitlesToDisplay],
  );

  const setLines = useCallback(
    (updatedLine) => {
      const newLines = linesData.map((line) => {
        if (line.num === updatedLine.num) {
          return updatedLine;
        }

        return line;
      });
      setLinesData(newLines);
    },
    [linesData],
  );

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    if (
      subtitlesToDisplay &&
      subtitlesAvailable &&
      (!linesOriginalData?.lines || linesOriginalData?.id !== subtitlesToDisplay.id)
    ) {
      dispatch(fetchIntroSubtitlesLines(subtitlesToDisplay.id));
    }
  }, [linesOriginalData, dispatch, subtitlesAvailable, subtitlesToDisplay, isFocused]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    if (!linesOriginalData || (subtitlesToDisplay && subtitlesToDisplay.id !== linesOriginalData.id)) {
      setLinesData(null);
      setInitialLinesData(null);
      return;
    }

    if (linesData) {
      return;
    }

    const lines = formatSubtitles(linesOriginalData.lines);

    setLinesData(lines);
    setInitialLinesData(lines);
  }, [subtitlesToDisplay, linesData, linesOriginalData, isFocused]);

  return {
    subtitlesAvailable,
    initialLinesData,
    linesData,
    linesOriginalData,
    setLines,
  };
};

export default useIntroSubtitles;
